import type { ComponentDefinition, FormComponent, FormDocument, FormParameters, FormTemplate } from '@formbird/types';
import { createSignal, createEffect, onCleanup } from 'solid-js';
import { Subscription } from 'rxjs';
import { ValidationDisplay } from '@formbird/solid-shared';

interface TextBoxProps {
  document: FormDocument;
  template: FormTemplate;
  componentDefinition: ComponentDefinition;
  fieldValue: string;
  fieldName: string;
  previousFieldValue: string;
  key: number;
  formParameters: FormParameters;
}

export function TextBox(props: TextBoxProps) {
  const subs = new Subscription();

  // Create signals for reactive state
  const [input, setInput] = createSignal(props.fieldValue ?? '');
  const [componentDef, setComponentDef] = createSignal(props.componentDefinition);

  // Get the changed document service from global injector
  const changedDocumentService = (globalThis as any).FormbirdServiceInjector.get('ChangedDocumentService');

  // Subscribe to value changes from the service
  const docSub = changedDocumentService
    .watch(props.fieldName, props.document?.documentId, null)
    .subscribe((newValue: any) => {
      setInput(newValue ?? '');
    });
  subs.add(docSub);

  // Subscribe to component definition changes
  const compSub = changedDocumentService
    .watchComponentDef(props.fieldName, props.template?.documentId)
    .subscribe((compDef: any) => {
      const updated = {
        ...componentDef(),
        ...compDef
      };
      setComponentDef(updated);
    });
  subs.add(compSub);

  // Cleanup subscriptions when component is destroyed
  onCleanup(() => {
    subs.unsubscribe();
  });

  // Derived values
  const isDisabled = () => componentDef()?.enabled === false;
  const widthClass = () => componentDef()?.fullWidth ? 'w-full' : 'w-72';

  // Effect to handle value changes
  createEffect(() => {
    if (isDisabled()) return;

    const changeInfo: FormComponent = {
      document: props.document,
      template: props.template,
      componentDefinition: componentDef(),
      fieldValue: input(),
      fieldName: props.fieldName,
      formParameters: props.formParameters,
      key: props.key,
    };

    changedDocumentService.valueChanged(changeInfo, input());
  });

  const handleInput = (e: Event) => {
    const target = e.target as HTMLInputElement;
    setInput(target.value);
  };

  return (
    <div class={`sb-text-box-container ${widthClass()}`}>
      <input
        type="text"
        id={`solid-text-box-${props.document?.documentId}`}
        placeholder=" "
        class={`sb-text-box-input ${isDisabled() ? 'cursor-not-allowed' : ''}`}
        value={input()}
        onInput={handleInput}
        disabled={isDisabled()}
      />
      <label
        for={`solid-text-box-${props.document?.documentId}`}
        class={`sb-text-box-label${componentDef()?.mandatory ? ' ft-mandatory-label' : ''}`}
      >
        {componentDef()?.label}
        {componentDef()?.mandatory && ' *'}
      </label>
      {props.document && props.componentDefinition && (
        <ValidationDisplay document={props.document} componentDefinition={componentDef()} />
      )}
    </div>
  );
}
